import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Send, Sparkles, Loader2, X } from "lucide-react";

const SUGGESTED = [
  "We are the provider",
  "It is used on EU users",
  "It makes decisions about people",
  "Do we need an Article 22 representative?",
];

export const Copilot = ({ input, answers, classification, onResult, messages, setMessages, onClose }) => {
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const content = (text ?? draft).trim();
    if (!content || busy) return;
    setDraft("");
    const next = [...messages, { role: "user", content }];
    setMessages(next);
    setBusy(true);
    try {
      const { data } = await api.post("/chat", {
        url: input.url,
        description: input.description,
        answers,
        classification,
        message: content,
        history: next,
      });
      setMessages([...next, { role: "assistant", content: data.reply }]);
      if (data.answers) {
        onResult(data.answers, data.changed);
      }
    } catch (e) {
      setMessages([
        ...next,
        { role: "assistant", content: "Sorry, I could not process that just now. Please try again shortly." },
      ]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="copilot-panel"
      className="flex h-full flex-col overflow-hidden bg-white"
    >
      <div className="flex items-center justify-between bg-asphalt px-4 py-3.5 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-aqua" />
          <span className="text-sm font-medium">Compliance copilot</span>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="copilot-close"
            className="rounded p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            aria-label="Close copilot"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div ref={scrollRef} className="gl-scroll flex-1 space-y-3 overflow-y-auto bg-tint p-4">
        {messages.length === 0 && (
          <div className="rounded-lg border border-line bg-white p-3 text-sm text-asphalt">
            Hi. I can explain this verdict and refine it with you. Tell me anything about your system, or
            tap a prompt below.
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
              m.role === "user"
                ? "ml-auto bg-asphalt text-white"
                : "border border-line bg-white text-asphalt"
            }`}
            data-testid={`copilot-msg-${m.role}`}
          >
            {m.content}
          </div>
        ))}
        {busy && (
          <div className="flex items-center gap-2 rounded-lg border border-line bg-white px-3 py-2 text-sm text-muted">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Thinking
          </div>
        )}
      </div>

      <div className="border-t border-line bg-white px-3 pt-2">
        <div className="flex flex-wrap gap-1.5 pb-2">
          {SUGGESTED.map((s) => (
            <button
              key={s}
              onClick={() => send(s)}
              disabled={busy}
              data-testid="copilot-chip"
              className="rounded-full border border-line px-2.5 py-1 text-xs text-asphalt transition-colors hover:border-asphalt/40 hover:bg-tint disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 border-t border-line bg-white p-3">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Tell the copilot something"
          data-testid="copilot-input"
          className="flex-1 rounded-brand border border-line px-3 py-2 text-sm text-asphalt outline-none focus:border-asphalt"
        />
        <button
          onClick={() => send()}
          disabled={busy}
          data-testid="copilot-send"
          className="gl-primary-btn flex items-center justify-center px-3.5 disabled:opacity-60"
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
